import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useGeolocationStore } from '@/stores/geolocation-store'
import { deriveFromPath, type ViewType, type WorkspaceType } from '../navigation'

/** Case-detail routing: derives the active workspace/view and the navigation
 *  callbacks passed down to sub-pages. */
export function useCaseNavigation(caseId: string) {
  const location = useLocation()
  const navigate = useNavigate()
  const setPreSelectedDocumentId = useGeolocationStore(s => s.setPreSelectedDocumentId)

  const { workspace, viewType } = useMemo(
    () => deriveFromPath(location.pathname),
    [location.pathname]
  )

  const navigateToView = useCallback(
    (type: ViewType) => {
      const pathMap: Record<ViewType, string> = {
        documents: `/cases/${caseId}/documents`,
        'investigate-overview': `/cases/${caseId}/insights`,
        'knowledge-graph': `/cases/${caseId}/explore-graph`,
        graph: `/cases/${caseId}/graph`,
        'entity-resolution': `/cases/${caseId}/entity-resolution`,
        identity: `/cases/${caseId}/identity`,
        geolocation: `/cases/${caseId}/geolocation`,
        'time-axis': `/cases/${caseId}/time-axis`,
      }
      navigate(pathMap[type], { replace: true })
    },
    [caseId, navigate]
  )

  const handleWorkspaceChange = useCallback(
    (target: WorkspaceType) => {
      const m: Record<WorkspaceType, string> = {
        overview: `/cases/${caseId}/overview`,
        documents: `/cases/${caseId}/documents`,
        investigate: `/cases/${caseId}/insights`,
        monitor: `/cases/${caseId}/monitor`,
        report: `/cases/${caseId}/case-summary`,
        agent: `/cases/${caseId}/agent`,
      }
      navigate(m[target], { replace: true })
    },
    [caseId, navigate]
  )

  const handleViewGeoData = useCallback(
    (documentId: string) => {
      if (documentId) setPreSelectedDocumentId(documentId)
      navigateToView('geolocation')
    },
    [setPreSelectedDocumentId, navigateToView]
  )

  return { workspace, viewType, navigateToView, handleWorkspaceChange, handleViewGeoData }
}
